/**
 * Day entry persistence. FR-001..FR-005, plus at-rest encryption from v0.4.
 *
 * The only thing this module stores is a DayEntry. Cycles, predictions and
 * statistics are derived by `@domain` at read time and never written back —
 * there is no derived-data cache here, and therefore no stale-derived-data bug
 * to chase later.
 *
 * ## Encryption, and why there is no date on disk
 *
 * Values are sealed under the data key (ADR-0006, `keyring.ts`). A stored record
 * is `{ id, sealed }` and nothing else: the id is opaque and the date lives
 * inside the ciphertext.
 *
 * That means queries cannot use an index, so the store builds a date-to-id map
 * once per unlock by decrypting everything, and answers every query from it.
 * This is the decrypt-all-on-unlock model ADR-0006 described, and it is
 * affordable for exactly the reason the ADR gave: a decade of daily entries is a
 * few thousand records (data model Section 8).
 *
 * Until v0.4 a plaintext `dateIndex` field made range queries cheap and leaked
 * which days had an entry. Not what any of them said — but a pattern of logged
 * dates *is* a cycle history, and the threat model rates the pattern of use as
 * critical (Section 1). The index is gone and a v0.4 database is migrated in
 * `db.ts`.
 *
 * ## Locked means empty, not an error
 *
 * With no key there is nothing to read, and every read here returns nothing. A
 * locked app has no business producing entries; callers must not treat it as a
 * failure.
 */
import { isISODate } from '@domain/date'
import type { DayEntry, ISODate, Timestamp } from '@domain/types'
import { ENTRY_SCHEMA_VERSION } from '@domain/types'
import { openDb, promisify, STORE, txDone, type StoredEntry } from './db'
import { open as openSealed, seal } from './crypto'
import { currentDek } from './keyring'
import { getDeviceId } from './deviceId'
import { notifyEntryWritten } from './syncBridge'

/** Opaque, unguessable, and carrying no meaning. See db.ts for why. */
function newId(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * The decrypted entry set, held while unlocked.
 *
 * Keyed against the CryptoKey it was built from rather than invalidated by a
 * callback from `keyring.ts`. That avoids a circular dependency and is also
 * simply correct: a different key means different data, and no key means no data.
 * Locking drops the key, so the cache is invalid the instant the app locks
 * without anything having to remember to clear it.
 */
interface Cache {
  dek: CryptoKey
  /** date -> record id */
  ids: Map<ISODate, string>
  entries: Map<ISODate, DayEntry>
}

let cache: Cache | undefined

/**
 * Build or reuse the decrypted set.
 *
 * Returns undefined when locked. A record that fails to decrypt is skipped
 * rather than thrown: that means it was written under a key that no longer
 * exists — after a keyring wipe, say — and one unreadable record must not take
 * down the read of every other day.
 */
async function load(): Promise<Cache | undefined> {
  const dek = currentDek()
  if (dek === undefined) {
    cache = undefined
    return undefined
  }
  if (cache?.dek === dek) return cache

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readonly')
  const rows = await promisify<StoredEntry[]>(tx.objectStore(STORE.entries).getAll())

  const ids = new Map<ISODate, string>()
  const entries = new Map<ISODate, DayEntry>()
  const legacy: StoredEntry[] = []

  for (const row of rows) {
    let entry: DayEntry | undefined
    if (row.payload !== undefined) {
      entry = row.payload
      legacy.push(row)
    } else if (row.sealed !== undefined) {
      try {
        entry = await openSealed<DayEntry>(row.sealed, dek)
      } catch {
        continue
      }
    }
    if (entry === undefined) continue
    ids.set(entry.date, row.id)
    entries.set(entry.date, entry)
  }

  cache = { dek, ids, entries }

  // Lazy migration of pre-v0.4 plaintext records (data model Section 4). After
  // one full read a v0.3 database holds no plaintext entries.
  if (legacy.length > 0) await resealLegacy(dek, legacy)

  return cache
}

async function resealLegacy(dek: CryptoKey, legacy: StoredEntry[]): Promise<void> {
  const sealedRecords = await Promise.all(
    legacy.map(async (record) => ({
      id: record.id,
      sealed: await seal(record.payload as DayEntry, dek),
    })),
  )

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  const store = tx.objectStore(STORE.entries)
  for (const record of sealedRecords) store.put(record)
  // Best effort: the plaintext record is still readable, so a failure here leaves
  // the app working and the next read tries again. Better than failing the read
  // the user actually asked for.
  await txDone(tx).catch(() => undefined)
}

const byDate = (a: DayEntry, b: DayEntry) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)

export async function getEntry(date: ISODate): Promise<DayEntry | undefined> {
  return (await load())?.entries.get(date)
}

/** Entries in `[from, to]`, inclusive, sorted ascending. */
export async function getEntriesInRange(from: ISODate, to: ISODate): Promise<DayEntry[]> {
  const loaded = await load()
  if (loaded === undefined) return []
  return [...loaded.entries.values()]
    .filter((entry) => entry.date >= from && entry.date <= to)
    .sort(byDate)
}

export async function getAllEntries(): Promise<DayEntry[]> {
  const loaded = await load()
  if (loaded === undefined) return []
  return [...loaded.entries.values()].sort(byDate)
}

/**
 * A partial update.
 *
 * `undefined` is explicit and meaningful: it CLEARS the field. That is how
 * "no flow today" is distinguished from "flow unchanged", which is why the
 * type spells out `| undefined` rather than relying on optionality —
 * `exactOptionalPropertyTypes` makes the two genuinely different.
 */
export type EntryPatch = {
  [K in keyof Omit<DayEntry, 'v' | 'date' | 'meta'>]?: DayEntry[K] | undefined
}

/**
 * Create or update the entry for `date`.
 *
 * Merges rather than replaces, because the UI commits each control on change
 * (there is no Save button to forget — UI design Section 4) and two controls
 * must not clobber each other.
 */
export async function putEntry(date: ISODate, patch: EntryPatch): Promise<DayEntry> {
  if (!isISODate(date)) throw new TypeError(`Not an ISO date: ${JSON.stringify(date)}`)

  const loaded = await load()
  if (loaded === undefined) {
    // Writing plaintext here would silently undo encryption at exactly the
    // moment the user believes they are protected.
    throw new Error('Cannot write an entry while locked')
  }

  const existing = loaded.entries.get(date)
  const now = Date.now()

  // Built by mutation rather than by spreading `patch`, because a spread would
  // write `flow: undefined` as a present key. With `exactOptionalPropertyTypes`
  // that is a different thing from an absent key — and it is a real difference
  // here, not a type-system quibble: a present-but-undefined field survives into
  // the export (FR-006) and into a sealed record over sync. "Cleared" and
  // "never set" must be indistinguishable once written.
  /*
   * `fieldTimes` carries one timestamp per field this device has ever written,
   * and it is stamped ALWAYS — not only once sync is enabled, which is what the
   * data model originally said.
   *
   * The per-field merge in `domain/merge.ts` is built on it: `touched()` asks
   * whether `fieldTimes[field]` exists, and the rule that a side which never
   * touched a field has no claim on it — the subtlest rule there, and the one
   * that cost two bugs to get right — can only fire when both sides carry these
   * stamps. Without them every field falls back to the record's `updatedAt`, so
   * per-field resolution silently degrades to last-write-wins for the whole
   * record. The merge tests passed throughout, because they construct their own
   * entries; nothing checked that the app actually produces this shape.
   *
   * Stamping only when sync is on would have left every entry written before
   * that moment unmergeable, and the first sync is exactly when a device meets
   * another device's history. The cost of always is a few dozen bytes inside an
   * already-sealed record.
   */
  const fieldTimes: Record<string, Timestamp> = { ...existing?.meta.fieldTimes }
  // Every key in the patch, including one being CLEARED: a cleared field is still
  // a field this device decided about. Skipping those would let a clear lose to a
  // stale remote value that nobody touched, silently undoing the deletion.
  for (const key of Object.keys(patch)) fieldTimes[key] = now

  const payload = {
    ...(existing ?? {}),
    v: ENTRY_SCHEMA_VERSION,
    date,
    meta: {
      createdAt: existing?.meta.createdAt ?? now,
      updatedAt: now,
      updatedBy: getDeviceId(),
      fieldTimes,
    },
  } as DayEntry

  for (const [key, value] of Object.entries(patch) as [keyof EntryPatch, unknown][]) {
    if (value === undefined) delete payload[key]
    else Object.assign(payload, { [key]: value })
  }

  const id = loaded.ids.get(date) ?? newId()
  const record: StoredEntry = { id, sealed: await seal(payload, loaded.dek) }

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  tx.objectStore(STORE.entries).put(record)
  await txDone(tx)

  loaded.ids.set(date, id)
  loaded.entries.set(date, payload)

  // A local edit, so it is offered to sync. Fire-and-forget by design: the write
  // has already succeeded and must not be made to wait on a relay.
  notifyEntryWritten(payload)

  return payload
}

/**
 * Store a complete entry exactly as given, without restamping its metadata.
 *
 * This is the inbound half of sync and the ONLY writer that may set `meta`
 * directly. `putEntry` stamps `updatedAt: now` and `updatedBy: <this device>` on
 * every write, which is right for a local edit and wrong for a merge result: it
 * would claim this device made the change, at this moment, and it would discard
 * the `fieldTimes` that `mergeEntries` just computed.
 *
 * That matters beyond bookkeeping. A third device merging the same day later
 * re-runs the same comparison, and it can only reach the same answer if the
 * timestamps and the device id it compares are the ORIGINAL ones. Restamping
 * makes the merge non-deterministic across devices, which is the failure where
 * two phones disagree forever and neither is wrong.
 *
 * Deliberately not exported through `index.ts` for general use — a caller
 * reaching for this instead of `putEntry` is almost certainly making a local edit
 * look like a remote one.
 */
export async function putMergedEntry(entry: DayEntry): Promise<DayEntry> {
  if (!isISODate(entry.date)) {
    throw new TypeError(`Not an ISO date: ${JSON.stringify(entry.date)}`)
  }

  const loaded = await load()
  if (loaded === undefined) throw new Error('Cannot write an entry while locked')

  const id = loaded.ids.get(entry.date) ?? newId()
  const record: StoredEntry = { id, sealed: await seal(entry, loaded.dek) }

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  tx.objectStore(STORE.entries).put(record)
  await txDone(tx)

  loaded.ids.set(entry.date, id)
  loaded.entries.set(entry.date, entry)

  /*
   * Deliberately does NOT notify the sync bridge.
   *
   * This is the inbound half of sync. Announcing it would send the record
   * straight back, the far device would merge and send again, and two devices
   * would keep each other busy indefinitely over data neither had changed.
   *
   * Convergence does not need the echo: each device merges the other's original
   * writes, and the merge is deterministic, so both reach the same result without
   * either broadcasting its conclusion.
   */
  return entry
}

/**
 * Mark a day as checked with nothing to record (FR-004).
 *
 * Distinct from having no entry at all. "Nothing happened" and "nobody looked"
 * are different facts, and the difference is what makes a gap in the data
 * meaningful.
 */
export function putEmptyEntry(date: ISODate): Promise<DayEntry> {
  return putEntry(date, {})
}

export async function deleteEntry(date: ISODate): Promise<void> {
  const loaded = await load()
  const id = loaded?.ids.get(date)
  if (loaded === undefined || id === undefined) return

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  tx.objectStore(STORE.entries).delete(id)
  await txDone(tx)

  loaded.ids.delete(date)
  loaded.entries.delete(date)
}

/** Delete every entry whose date falls in `[from, to]`, inclusive. FR-607. */
export async function deleteEntriesInRange(from: ISODate, to: ISODate): Promise<number> {
  const loaded = await load()
  if (loaded === undefined) return 0

  const doomed = [...loaded.entries.keys()].filter((date) => date >= from && date <= to)
  if (doomed.length === 0) return 0

  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  const store = tx.objectStore(STORE.entries)
  for (const date of doomed) {
    const id = loaded.ids.get(date)
    if (id !== undefined) store.delete(id)
  }
  await txDone(tx)

  for (const date of doomed) {
    loaded.ids.delete(date)
    loaded.entries.delete(date)
  }
  return doomed.length
}

/**
 * Delete every entry. FR-607.
 *
 * A store clear rather than a scan-and-filter, which is why `entries` is kept
 * separate from settings and metadata (data model Section 3). Destroying the
 * keyring is a separate, deliberate step — see `wipeEverything`.
 */
export async function deleteAllEntries(): Promise<void> {
  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readwrite')
  tx.objectStore(STORE.entries).clear()
  await txDone(tx)
  cache = undefined
}

/** How many records exist on disk. Answerable while locked; reveals only a count. */
export async function countEntries(): Promise<number> {
  const db = await openDb()
  const tx = db.transaction(STORE.entries, 'readonly')
  return promisify(tx.objectStore(STORE.entries).count())
}

/** Test seam. Not used by application code. */
export function forgetEntryCacheForTests(): void {
  cache = undefined
}
